import {makeAutoObservable, reaction} from 'mobx';

import type {WithReactionDispose} from '@yourcoach/shared/utils/store';

import {currentUserStore} from '@yourcoach/shared/api/user';
import {getPartner, type Partner} from '@yourcoach/shared/partners';
import {
  FlagManager,
  ReactionDisposeManager,
} from '@yourcoach/shared/utils/store';

/**
 * The class stores data for B2C demo.
 *
 * To start the demo
 * - go to the /login?demo=partner_id
 * - default code is DEMO2026, if it's not exist, create it in the admin panel
 */
class B2CDemoStore implements WithReactionDispose {
  static ACTIVATION_PARAM = 'demo';
  static CO_PARTNER_PARAM = 'co';
  static COLOR_PARAM = 'color';
  clientName: null | string = null;
  code: null | string = null;
  readonly disposer = new ReactionDisposeManager();
  isActive = new FlagManager();
  isCoPartner = false;
  partner: null | Partner = null;
  private _color: null | string = null;

  constructor() {
    makeAutoObservable(this);

    this.setupReactions();
  }

  init(pid: string) {
    const partner = getPartner(pid);

    if (!partner) {
      return;
    }

    this.partner = partner;

    this.isActive.setTrue();
  }

  get color() {
    return this._color || this.partner?.color;
  }

  get searchString() {
    const parts = [
      [B2CDemoStore.ACTIVATION_PARAM, this.partner?.id],
      this._color
        ? [B2CDemoStore.COLOR_PARAM, this._color.replace('#', '')]
        : null,
      this.isCoPartner ? [B2CDemoStore.CO_PARTNER_PARAM, '1'] : null,
    ];

    return parts
      .filter(Boolean)
      .map(part => part!.join('='))
      .join('&');
  }

  setClientName(clientName: string) {
    this.clientName = clientName;
  }

  setCode(code: string) {
    this.code = code;
  }

  setColor(color: null | string) {
    if (!color) {
      this._color = null;

      return;
    }

    if (!color?.startsWith('#')) {
      color = `#${color}`;
    }

    // check if it's a valid hex color
    if (!/^#[\da-f]{6}$/i.test(color)) {
      return;
    }

    this._color = color;
  }

  setIsCoPartner(isCoPartner: boolean) {
    this.isCoPartner = isCoPartner;
  }

  private setupReactions() {
    this.disposer.watch(
      'code',
      reaction(
        () => currentUserStore.user,
        user => {
          // @see pages/Login/steps/VerificationStep.tsx#L377
          if (user?.metadata.b2c_demo?.code) {
            this.code = user.metadata.b2c_demo.code;
          }
        },
        {
          fireImmediately: true,
        },
      ),
    );
  }
}

export const b2cDemoStore = new B2CDemoStore();
